export interface TabVisibilityHandlerOptions {
    onVisible: () => void;
    onHidden?: () => void;
    debounceMs?: number;
    shouldExecute?: () => boolean;
    visibilityDocument?: Document;
}

export function getVisibilityDocument(): Document {
    try {
        if (window.top && window.top !== window && window.top.document) {
            return window.top.document;
        }
    } catch (e) {
        // Cross-origin iframe
    }
    return document;
}

/**
 * Creates a handler that executes a callback when tab becomes visible.
 * Returns a cleanup function to remove listeners and clear timers.
 *
 * @param options.onVisible - Callback to execute when tab becomes visible
 * @param options.onHidden - Optional callback to execute immediately when tab becomes hidden
 * @param options.debounceMs - Debounce time in ms (default: 0)
 * @param options.shouldExecute - Optional function to check if callback should execute (default: () => true)
 * @param options.visibilityDocument - Optional custom document to monitor (default: top-level or current document)
 */
export function createTabVisibilityHandler(
    options: TabVisibilityHandlerOptions
): () => void {
    const {
        onVisible,
        onHidden,
        debounceMs = 0,
        shouldExecute = () => true,
        visibilityDocument = getVisibilityDocument(),
    } = options;

    let debounceTimeout: ReturnType<typeof setTimeout> | null = null;
    let isDisposed = false;

    const handleVisibilityChange = () => {
        const isNowVisible = !visibilityDocument.hidden;

        if (debounceTimeout) {
            clearTimeout(debounceTimeout);
            debounceTimeout = null;
        }

        if (!isNowVisible) {
            // Execute onHidden callback immediately when tab becomes hidden
            if (onHidden && !isDisposed && shouldExecute()) {
                onHidden();
            }
            return;
        }

        const executeCallback = () => {
            if (isDisposed || visibilityDocument.hidden || !shouldExecute()) {
                return;
            }

            onVisible();
            debounceTimeout = null;
        };

        if (debounceMs > 0) {
            debounceTimeout = setTimeout(executeCallback, debounceMs);
        } else {
            executeCallback();
        }
    };

    visibilityDocument.addEventListener(
        "visibilitychange",
        handleVisibilityChange
    );

    return () => {
        isDisposed = true;

        if (debounceTimeout) {
            clearTimeout(debounceTimeout);
            debounceTimeout = null;
        }

        visibilityDocument.removeEventListener(
            "visibilitychange",
            handleVisibilityChange
        );
    };
}

export function isTabVisible(visibilityDocument?: Document): boolean {
    const doc = visibilityDocument || getVisibilityDocument();
    return !doc.hidden;
}
